import { TenantConfig } from '../config/tenant-config';
import { environment } from '../../../environments/environment';

export type TenantInitErrorCode =
  | 'TENANT_EXTRACTION_FAILED'
  | 'TENANT_VALIDATION_FAILED'
  | 'TENANT_API_ERROR'
  | 'TENANT_NOT_FOUND'
  | 'TENANT_INACTIVE';

export class TenantInitError extends Error {
  constructor(
    public readonly code: TenantInitErrorCode,
    message: string,
    public readonly tenantId?: string
  ) {
    super(message);
    this.name = 'TenantInitError';
  }
}

export interface TenantApiResponse {
  success: boolean;
  data: {
    id: string;
    tenantId: string;
    tenantName: string;
    displayName?: string;
    status: 'active' | 'inactive' | 'pending' | 'suspended';
    contactEmail?: string;
    contactPhone?: string;
    contactName?: string;
    address?: string;
    formattedAddress?: string;
    placeId?: string | null;
    isManualEntry?: boolean;
    createdAt?: any;
    estimatedStudents?: number;
    planId?: string;
    billingCycle?: string;
    trialDays?: number;
    domain?: string;
    approvedBy?: string;
    approvedAt?: any;
    firebaseMode?: 'shared' | 'separate';
    features?: {
      admissions?: boolean;
      payments?: boolean;
      classes?: boolean;
      communication?: boolean;
      [key: string]: any;
    };
    settings?: {
      allowPlatformAccess?: boolean;
      platformAccessEnabledAt?: any;
      platformAccessEnabledBy?: string;
      updatedAt?: any;
      [key: string]: any;
    };
    theme?: TenantConfig['theme'];
    branding?: {
      name?: string;
      logo?: string;
      colors?: {
        primary?: string;
        accent?: string;
        warn?: string;
        [key: string]: any;
      };
      primaryColor?: string;
      secondaryColor?: string;
      address?: string;
      contactName?: string;
      contactPhone?: string;
      contactEmail?: string;
      website?: string;
      principal?: string;
      workingHours?: string;
      academicYear?: string;
      registrationNumber?: string;
      affiliation?: string;
      establishedYear?: string;
      description?: string;
      updatedAt?: any;
      [key: string]: any;
    };
    firebaseConfig?: {
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
    };
    [key: string]: any;
  };
}

/**
 * Service for initializing tenant configuration before Angular bootstrap.
 * This is a plain class (not an Angular service) because it's instantiated
 * manually in main.ts before the Angular DI system is available.
 */
export class TenantInitService {
  /**
   * Extract tenant ID from runtime sources.
   * Priority order:
   * 1. Header bridge: window.__tenantHeader / window.__tenantId (Cloudflare sets this in production)
   * 2. Query parameter: ?tenantId=demo (used for local/dev environments)
   * 3. LocalStorage: tt_tenant_id (fallback)
   * 
   * Note: In production, Cloudflare middleware injects tenantId in headers.
   * In local/dev, tenantId is passed via query parameters.
   */
  extractTenantId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    if (window.location.pathname === '/error') {
      return null;
    }

    // Priority 1: Header bridge (Cloudflare sets this in production)
    const fromHeaderBridge = this.extractTenantFromHeaderBridge();
    if (fromHeaderBridge) {
      return fromHeaderBridge;
    }

    // Priority 2: Query parameter (used for local/dev environments)
    const urlParams = new URLSearchParams(window.location.search);
    const queryTenantId = (urlParams.get('tenantId') || urlParams.get('tenantid') || '').trim();
    if (queryTenantId) {
      return queryTenantId;
    }

    // Priority 3: LocalStorage (fallback)
    try {
      const storedTenantId = (window.localStorage.getItem('tt_tenant_id') || '').trim();
      if (storedTenantId) {
        return storedTenantId;
      }
    } catch {
      // ignore
    }

    return null;
  }

  private extractTenantFromHeaderBridge(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const directTenant = ((window as any).__tenantId || '').toString().trim();
    if (directTenant) {
      return directTenant;
    }

    const headerObject = (window as any).__tenantHeader;
    if (!headerObject || typeof headerObject !== 'object') {
      return null;
    }

    const headerTenant =
      headerObject['x-tenant-id'] ||
      headerObject['x-tenantid'] ||
      headerObject['X-Tenant-Id'] ||
      headerObject['tenantId'];

    return typeof headerTenant === 'string' ? headerTenant.trim() || null : null;
  }

  /**
   * Validate tenant by calling API and returning normalized tenant config.
   */
  async initializeTenant(tenantId?: string): Promise<TenantConfig> {
    const resolvedTenantId = (tenantId || this.extractTenantId() || '').trim();

    if (window.location.pathname === '/error') {
      throw new TenantInitError('TENANT_EXTRACTION_FAILED', 'Tenant extraction skipped on /error');
    }

    if (!resolvedTenantId) {
      throw new TenantInitError(
        'TENANT_EXTRACTION_FAILED',
        'Tenant ID could not be extracted from request context'
      );
    }

    const baseApiUrlValue =
      typeof environment.baseApiUrl === 'string'
        ? environment.baseApiUrl
        : String(environment.baseApiUrl);

    if (!baseApiUrlValue || baseApiUrlValue.trim() === '') {
      throw new TenantInitError(
        'TENANT_API_ERROR',
        'baseApiUrl is not configured in environment'
      );
    }

    const baseUrl = baseApiUrlValue.endsWith('/api')
      ? baseApiUrlValue
      : `${baseApiUrlValue}/api`;
    const accountsUrl = `${baseUrl}/auth/accounts`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': resolvedTenantId,
      'X-App-Id': 'web-app',
    };

    try {
      const response = await fetch(accountsUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any));
        const errorMessage = String(errorData?.error || `Failed to validate tenant: ${response.statusText}`);

        if (response.status === 404 || /not found/i.test(errorMessage)) {
          throw new TenantInitError('TENANT_NOT_FOUND', errorMessage, resolvedTenantId);
        }

        if (response.status === 400 && /not active|inactive/i.test(errorMessage)) {
          throw new TenantInitError('TENANT_INACTIVE', errorMessage, resolvedTenantId);
        }

        throw new TenantInitError('TENANT_VALIDATION_FAILED', errorMessage, resolvedTenantId);
      }

      const apiResponse: TenantApiResponse = await response.json();

      if (!apiResponse.success || !apiResponse.data) {
        throw new TenantInitError(
          'TENANT_VALIDATION_FAILED',
          'Invalid account response format',
          resolvedTenantId
        );
      }

      const tenantData = apiResponse.data;
      if (tenantData.status !== 'active') {
        throw new TenantInitError(
          'TENANT_INACTIVE',
          `Tenant '${resolvedTenantId}' is not active`,
          resolvedTenantId
        );
      }

      return {
        tenantId: tenantData.tenantId,
        tenantName: tenantData.tenantName || tenantData.displayName || '',
        firebaseMode: tenantData.firebaseMode || (tenantData.firebaseConfig ? 'separate' : 'shared'),
        appMode: tenantData['appMode'] === 'full' ? 'full' : 'admin',
        firebaseConfig: tenantData.firebaseConfig,
        features: tenantData.features,
        theme: tenantData.theme,
        branding: tenantData.branding || {
          name: tenantData.displayName || tenantData.tenantName,
          address: tenantData.address || tenantData.formattedAddress,
          contactName: tenantData.contactName,
          contactPhone: tenantData.contactPhone,
          contactEmail: tenantData.contactEmail,
        },
        domain: tenantData.domain,
      };
    } catch (error: any) {
      if (error instanceof TenantInitError) {
        throw error;
      }

      throw new TenantInitError(
        'TENANT_API_ERROR',
        error?.message || 'Unable to connect to tenant account service',
        resolvedTenantId
      );
    }
  }
}
